import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../config/theme';
import PaymentMethodCard from '../PaymentMethodCard';
import StaxCardForm from '../StaxCardForm';
import { staxConfig } from '../../../../config/stax';
import Modal from '../../ui/Modal';

const Container = styled.div`
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
  position: relative;
  justify-content: center;
  
  &::before {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.neutral.light};
    margin-right: ${theme.spacing.md};
  }
  
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.neutral.light};
    margin-left: ${theme.spacing.md};
  }
`;

const Title = styled.h3`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 0.95rem;
  font-weight: 600;
  color: ${theme.colors.primary.text};
  margin: 0;
  white-space: nowrap;
`;

const SectionContent = styled.div`
  padding-left: 0;
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr auto;
  gap: ${theme.spacing.md};
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 100px 1fr auto;
    gap: ${theme.spacing.sm};
  }
`;

const InfoLabel = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${theme.colors.primary.text};
  opacity: 0.7;
`;

const InfoValue = styled.span`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.primary.text};
`;

const SmallButton = styled.button`
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  
  background: transparent;
  color: ${theme.colors.primary.buttons};
  border: 1px solid ${theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-size: 0.75rem;
  min-height: 32px;
  height: 32px;
  cursor: pointer;
  transition: ${theme.transitions.default};
  text-decoration: none;
  
  &:hover:not(:disabled) {
    background: ${theme.colors.primary.buttons};
    color: ${theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;


// Removed EmptyState components - now using InfoRow pattern

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.xl} 0;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid ${theme.colors.neutral.light};
    border-top-color: ${theme.colors.primary.buttons};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
  padding: ${theme.spacing.md};
  border-radius: 8px;
  margin-bottom: ${theme.spacing.md};
`;

const ModalTitle = styled.h2`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 400;
  color: ${theme.colors.primary.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  text-align: center;
`;

const PaymentMethodsList = ({
  paymentMethods = [],
  isLoading = false,
  error = null,
  onAddPaymentMethod,
  onDeletePaymentMethod,
  onSetDefaultPaymentMethod,
  staxPublicKey
}) => {
  const [showAddCard, setShowAddCard] = useState(false);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [deletingCardId, setDeletingCardId] = useState(null);

  const handleAddCard = async (cardData) => {
    if (!onAddPaymentMethod) return;

    setIsAddingCard(true);
    try {
      await onAddPaymentMethod(cardData);
      setShowAddCard(false);
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw error;
    } finally {
      setIsAddingCard(false);
    }
  };

  const handleDeleteCard = async (paymentMethodId) => {
    if (!onDeletePaymentMethod) return;

    setDeletingCardId(paymentMethodId);
    try {
      await onDeletePaymentMethod(paymentMethodId);
    } catch (error) {
      console.error('Error deleting payment method:', error);
    } finally {
      setDeletingCardId(null);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    if (!onSetDefaultPaymentMethod) return;

    try {
      await onSetDefaultPaymentMethod(paymentMethodId);
    } catch (error) {
      console.error('Error setting default payment method:', error);
    }
  };

  if (isLoading) {
    return (
      <Container>
        <SectionHeader>
          <Title>Payment Methods</Title>
        </SectionHeader>
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeader>
        <Title>Payment Methods</Title>
      </SectionHeader>

      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}

      <SectionContent>
        {paymentMethods.length === 0 ? (
          <InfoRow>
            <InfoLabel>Cards</InfoLabel>
            <InfoValue>No payment methods</InfoValue>
            <SmallButton 
              onClick={() => setShowAddCard(true)}
              disabled={!staxPublicKey}
            >
              Add Card
            </SmallButton>
          </InfoRow>
        ) : (
          <>
            {paymentMethods.map((method) => (
              <PaymentMethodCard
                key={method.id}
                paymentMethod={method}
                onDelete={onDeletePaymentMethod ? handleDeleteCard : null}
                onSetDefault={onSetDefaultPaymentMethod ? handleSetDefault : null}
                isDeleting={deletingCardId === method.id}
                brandLogos={staxConfig.cardBrandLogos}
              />
            ))}
            <InfoRow style={{ marginTop: theme.spacing.md }}>
              <InfoLabel></InfoLabel>
              <InfoValue></InfoValue>
              <SmallButton 
                onClick={() => setShowAddCard(true)}
                disabled={!staxPublicKey}
              >
                Add Card
              </SmallButton>
            </InfoRow>
          </>
        )}
      </SectionContent>

      {showAddCard && (
        <Modal 
          isOpen={showAddCard}
          onClose={() => !isAddingCard && setShowAddCard(false)}
          maxWidth="550px"
          theme={theme}
        >
          <ModalTitle>Add Payment Method</ModalTitle>
          <StaxCardForm
            onSuccess={handleAddCard}
            onCancel={() => setShowAddCard(false)}
            setAsDefault={paymentMethods.length === 0}
          />
        </Modal>
      )}
    </Container>
  );
};

export default PaymentMethodsList;